import { ChangeDetectionStrategy, Component, computed, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LookupResponse } from '@core/models/common.model';
import { lookupMetadata } from '@core/lookup/lookup-metadata';

type ScaleMetadata = { value?: number; color?: string };

// A slider over the levels of a lookup list (the "slider" of the counselling intake: duration, severity, impact, patterns,
// risk). The track is split into one coloured segment per level and the thumb sits on the chosen one; it can be clicked,
// dragged or moved with the arrow keys. The colour and the number of each level are in its item metadata.
//
// The value is the id of the chosen item, or '' until one is chosen (the thumb is hidden until then). With `numeric` it is the
// item's number instead (or null), for the ratings that are stored as numbers. `minValue` leaves out the levels below it (a
// 1 to 5 rating does not offer "Not applicable"), and `clearable` lets the choice be taken back.
@Component({
  selector: 'scale-slider',
  templateUrl: './scale-slider.component.html',
  // A slider that is required and has not been set, once the person has tried to go on.
  styles: [":host(.ng-invalid.ng-touched) [role='slider'] { outline: 2px solid #dc2626; outline-offset: 3px; }"],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ScaleSliderComponent),
      multi: true,
    },
  ],
})
export class ScaleSliderComponent implements ControlValueAccessor {
  readonly label = input.required<string>();
  readonly options = input<LookupResponse[]>([]);
  readonly required = input(false);
  readonly numeric = input(false);
  readonly minValue = input<number | null>(null);
  readonly clearable = input(false);
  readonly showDescription = input(false);

  // The chosen level's id, or its number as text when numeric; '' when nothing is chosen.
  protected readonly value = signal('');
  protected readonly disabled = signal(false);
  private dragging = false;

  // The levels in the order of their value, with the colour each one is shown in.
  protected readonly levels = computed(() => {
    const minimum = this.minValue();
    return this.options()
      .map((item) => {
        const metadata = lookupMetadata<ScaleMetadata>(item);
        return {
          id: item.id,
          name: item.name ?? item.code ?? '',
          description: item.description ?? null,
          value: metadata?.value ?? 0,
          color: metadata?.color ?? '#6B7280',
        };
      })
      .filter((level) => minimum === null || level.value >= minimum)
      .sort((a, b) => a.value - b.value);
  });

  protected readonly selectedIndex = computed(() => this.levels().findIndex((level) => this.keyOf(level) === this.value()));
  protected readonly selected = computed(() => this.levels()[this.selectedIndex()] ?? null);

  // The thumb sits in the middle of the chosen segment.
  protected readonly thumbLeft = computed(() => {
    const count = this.levels().length;
    return count > 0 && this.selectedIndex() >= 0 ? ((this.selectedIndex() + 0.5) / count) * 100 : 0;
  });

  private onChange: (value: string | number | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  private keyOf(level: { id: string; value: number }): string {
    return this.numeric() ? String(level.value) : level.id;
  }

  writeValue(value: string | number | null): void {
    this.value.set(value === null || value === undefined ? '' : String(value));
  }

  registerOnChange(fn: (value: string | number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected clear(): void {
    this.value.set('');
    this.onChange(this.numeric() ? null : '');
    this.onTouched();
  }

  protected pointerDown(event: PointerEvent): void {
    if (this.disabled()) {
      return;
    }

    this.dragging = true;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.chooseAt(event);
  }

  protected pointerMove(event: PointerEvent): void {
    if (this.dragging) {
      this.chooseAt(event);
    }
  }

  protected pointerUp(event: PointerEvent): void {
    this.dragging = false;
    (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
    this.onTouched();
  }

  protected keyDown(event: KeyboardEvent): void {
    const last = this.levels().length - 1;
    if (this.disabled() || last < 0) {
      return;
    }

    const current = this.selectedIndex();
    let target: number | null = null;

    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      target = Math.min(current + 1, last);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      target = Math.max(current - 1, 0);
    } else if (event.key === 'Home') {
      target = 0;
    } else if (event.key === 'End') {
      target = last;
    } else if ((event.key === 'Delete' || event.key === 'Backspace') && this.clearable()) {
      event.preventDefault();
      this.clear();
    }

    if (target !== null) {
      event.preventDefault();
      this.choose(target);
    }
  }

  private chooseAt(event: PointerEvent): void {
    const bounds = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const count = this.levels().length;
    if (count === 0 || bounds.width === 0) {
      return;
    }

    const fraction = Math.min(Math.max((event.clientX - bounds.left) / bounds.width, 0), 0.9999);
    this.choose(Math.floor(fraction * count));
  }

  private choose(index: number): void {
    const level = this.levels()[index];
    if (!level || this.keyOf(level) === this.value()) {
      return;
    }

    this.value.set(this.keyOf(level));
    this.onChange(this.numeric() ? level.value : level.id);
  }
}
